import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  InputRightAddon,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { DesignContestField } from "../components/DesignContestField";
import { isDesignContest, offersDesignContest } from "../utils/designContest";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ComingSoonModal } from "../../../../../common/ComingSoonModal";
import { FormLabelWithInfo } from "../../../../../common/input/FormLabelWithInfo";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ProcedureType,
  Regulation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import {
  procedureProvisionKey,
  procedureTypeOptionsFor,
  requiresProcedureJustification,
  type ProcedureTypeOption,
} from "../utils/publicProcurementOptions";
import {
  allowsNegotiation,
  isDpsCompetition,
  isDpsScheme,
  isQualificationScheme,
  isTwoStageProcedure,
  offersAwardOnInitialTenders,
  runsBelowEeaThreshold,
} from "../utils/procedure";
import { isAtOrAboveEea, isFoaNationalTier, minCandidatesWhenCapped } from "../utils/karens";
import { CandidateLimitsField } from "../components/CandidateLimitsField";
import { DpsSchemeFields } from "../components/DpsSchemeFields";
import { QualificationSchemeFields } from "../components/QualificationSchemeFields";
import { PriorInformationCallField } from "../components/PriorInformationCallField";
import { NegotiationTermsField } from "../components/NegotiationTermsField";
import { DialoguePlanField } from "../components/DialoguePlanField";
import { isFoaPartII } from "../utils/foaPartII";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { SubmissionLanguagesField } from "../components/SubmissionLanguagesField";
import { MilestonePlanCard } from "../components/MilestonePlanCard";
import { NoCompetitionJustificationField } from "../components/NoCompetitionJustificationField";
import { NegotiationWithoutNoticeField } from "../components/NegotiationWithoutNoticeField";
import { isNegotiationWithoutNotice } from "../utils/negotiationWithoutNotice";
import { followsDesignContestBelowEeaTier, isWithoutCompetition, offersWithoutCompetition } from "../utils/noCompetition";
import { QualificationSchemeInvitees } from "../components/qualificationScheme/QualificationSchemeInvitees";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ProcedureStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const validityError = fieldError("publicContext.eFormsNoticeData.tenderValidityMonths");

  const validityMonths = publicContext?.eFormsNoticeData?.tenderValidityMonths ?? null;
  const [validityStr, setValidityStr] = useState<string>(validityMonths != null ? String(validityMonths) : "");
  const [comingSoon, setComingSoon] = useState<string | null>(null);

  const isSaving = (field: string) => {
    const s = fieldStatus(field);
    return s === "saving" || s === "pending";
  };

  const procedureType = publicContext?.procedureType ?? "";
  const regulation = publicContext?.regulation ?? null;
  const procedureOptions = procedureTypeOptionsFor(regulation, publicContext?.foaPart);
  const overEeaThreshold = isAtOrAboveEea(regulation, publicContext?.foaPart);
  const meetsPartRequirement = (value: ProcedureType) =>
    !isTwoStageProcedure(value) || overEeaThreshold || runsBelowEeaThreshold(value);
  const optionEnabled = (option: ProcedureTypeOption) => option.enabled && meetsPartRequirement(option.value);
  const optionBarredByTier = (option: ProcedureTypeOption) => !meetsPartRequirement(option.value);
  const optionComingSoon = (option: ProcedureTypeOption) => !option.enabled && !optionBarredByTier(option);
  const eeaTierLabel =
    regulation === Regulation.Forsyningsforskriften
      ? t("Requires part II or above (over the EEA threshold)")
      : t("Requires FOA part III (above the EEA threshold)");
  const dpsBarredLabel = isFoaPartII(regulation, publicContext?.foaPart)
    ? t("Not available in FOA part II — choose open or restricted tender competition")
    : eeaTierLabel;

  // The dynamiske innkjøpsordning is FOA § 26-5 to § 26-7 throughout; forsyningsforskriften's
  // counterpart is the kvalifikasjonsordning (kap. 13), so each regulation offers its own card.
  const underUtilities = regulation === Regulation.Forsyningsforskriften;
  const withoutCompetition = isWithoutCompetition(publicContext?.procedureType);
  const showsWithoutCompetition = withoutCompetition || offersWithoutCompetition(regulation, publicContext?.foaPart);
  const withoutNotice = isNegotiationWithoutNotice(publicContext?.procedureType);
  const showsWithoutNotice = withoutNotice || offersWithoutCompetition(regulation, publicContext?.foaPart);
  const designContest = isDesignContest(publicContext?.procedureType);
  const offersContest = designContest || offersDesignContest(
    regulation,
    publicContext?.foaPart,
    publicContext?.estimatedValue?.value,
  );
  const contestBarred = !offersContest && !overEeaThreshold;
  const showsDesignContest = offersContest || contestBarred;
  const contestBarredLabel = underUtilities ? eeaTierLabel : t("zz_FieldErrorCode_DesignContestBelowPartV");

  const underQualificationScheme = publicContext?.qualificationSchemeCompetition;
  if (underQualificationScheme && publicContext?.procedureType) {
    return (
      <Box pb="8" pt="10">
        <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="lg" p="5" mb="8">
          <Text fontWeight="semibold" fontSize="sm" color="smText">
            {t("Competition under a qualification system")}: {t(publicContext.procedureType)}
          </Text>
          {underQualificationScheme.schemeTitle && (
            <Text fontSize="xs" color="smMuted" mt="1">
              {t("Part of {{scheme}}.", { scheme: underQualificationScheme.schemeTitle })}
            </Text>
          )}
          <Box mt="3">
            <QualificationSchemeInvitees eventId={eventId} />
          </Box>
        </Box>

        <MilestonePlanCard event={state} />

        <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      </Box>
    );
  }

  // FOA § 26-7 settled the procedure at the announcement — only this purchase's terms are left.
  if (isDpsCompetition(publicContext?.procedureType)) {
    return (
      <Box pb="8" pt="10">
        <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="lg" p="5" mb="8">
          <Text fontWeight="semibold" fontSize="sm" color="smText">
            {t("Competition under a dynamic purchasing system")}
          </Text>
          <Text fontSize="xs" color="smMuted" mt="1">
            {publicContext?.dpsCompetition?.schemeTitle
              ? t("Part of {{scheme}}. Every supplier qualified when you open it is invited.", {
                  scheme: publicContext.dpsCompetition.schemeTitle,
                })
              : t("Every supplier qualified when you open it is invited.")}
          </Text>
        </Box>

        <MilestonePlanCard event={state} />

        <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      </Box>
    );
  }

  return (
    <Box pb="8" pt="10">
      <FormControl mb="8">
        <FormLabel>
          {t("Procedure type")} {isSaving("publicContext.procedureType") && <Spinner size="xs" />}
        </FormLabel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
          {procedureOptions.map((option) => {
            const provisionKey = procedureProvisionKey(regulation, option.value);
            return (
            <SelectableCard
              key={option.value}
              isSelected={procedureType === option.value}
              isDisabled={optionBarredByTier(option)}
              disabledLabel={eeaTierLabel}
              onClick={() => {
                if (optionComingSoon(option)) {
                  setComingSoon(t(option.value));
                } else if (optionEnabled(option)) {
                  updateField({ publicContext: { procedureType: option.value } });
                }
              }}
            >
              <Text fontWeight="semibold" fontSize="sm" color={optionBarredByTier(option) ? "smMuted" : "smText"}>
                {t(option.value)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t(option.descriptionKey)}
              </Text>
              {provisionKey && (
                <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
                  {t(provisionKey)}
                </Text>
              )}
            </SelectableCard>
            );
          })}
          {underUtilities && procedureType === ProcedureType.DynamicPurchasingSystem && (
            <SelectableCard
              key="dynamic-purchasing-system-stale"
              isSelected
              isDisabled
              disabledLabel={t(
                "The FOA dynamic purchasing system is not available under this regulation — pick another procedure",
              ) ?? undefined}
              gridColumn={{ md: "1 / -1" }}
              onClick={() => undefined}
            >
              <Text fontWeight="semibold" fontSize="sm" color="smMuted">
                {t("Dynamic purchasing system (DPS)")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("Continuous qualification of suppliers")}
              </Text>
            </SelectableCard>
          )}
          {underUtilities ? (
            <SelectableCard
              key="qualification-scheme"
              isSelected={procedureType === ProcedureType.QualificationScheme}
              isDisabled={!overEeaThreshold}
              disabledLabel={eeaTierLabel}
              gridColumn={{ md: "1 / -1" }}
              onClick={() => {
                if (overEeaThreshold) {
                  updateField({ publicContext: { procedureType: ProcedureType.QualificationScheme } });
                }
              }}
            >
              <Text
                fontWeight="semibold"
                fontSize="sm"
                color={overEeaThreshold ? "smText" : "smMuted"}
              >
                {t("Qualification system")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("A standing supplier list — competitions run among those admitted")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
                {t("Announced once and open for its whole validity — forsyningsforskriften § 13-1")}
              </Text>
            </SelectableCard>
          ) : (
            <SelectableCard
              key="dynamic-purchasing-system"
              isSelected={procedureType === ProcedureType.DynamicPurchasingSystem}
              isDisabled={!overEeaThreshold}
              disabledLabel={dpsBarredLabel}
              gridColumn={{ md: "1 / -1" }}
              onClick={() => {
                if (overEeaThreshold) {
                  updateField({ publicContext: { procedureType: ProcedureType.DynamicPurchasingSystem } });
                }
              }}
            >
              <Text
                fontWeight="semibold"
                fontSize="sm"
                color={overEeaThreshold ? "smText" : "smMuted"}
              >
                {t("Dynamic purchasing system (DPS)")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("Continuous qualification of suppliers")}
              </Text>
            </SelectableCard>
          )}
          {showsWithoutNotice && (
            <SelectableCard
              key="negotiation-without-notice"
              isSelected={withoutNotice}
              gridColumn={{ md: "1 / -1" }}
              onClick={() =>
                updateField({ publicContext: { procedureType: ProcedureType.NegotiatedWithoutPriorCall } })
              }
            >
              <Text fontWeight="semibold" fontSize="sm" color="smText">
                {t(ProcedureType.NegotiatedWithoutPriorCall)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("Negotiate with suppliers you invite, without announcing the competition, when the regulation allows it")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
                {underUtilities ? "forsyningsforskriften § 9-2" : "FOA § 13-3"}
              </Text>
            </SelectableCard>
          )}
          {showsDesignContest && (
            <SelectableCard
              key="design-contest"
              isSelected={designContest}
              isDisabled={contestBarred}
              disabledLabel={contestBarredLabel}
              gridColumn={{ md: "1 / -1" }}
              onClick={() =>
                !designContest &&
                !contestBarred &&
                updateField({ publicContext: { procedureType: ProcedureType.DesignContestOpen } })
              }
            >
              <Text fontWeight="semibold" fontSize="sm" color={contestBarred ? "smMuted" : "smText"}>
                {t("Design contest")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("A jury chooses the best plan or design among anonymous entries")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
                {underUtilities ? "forsyningsforskriften kap. 27" : "FOA kap. 31"}
              </Text>
            </SelectableCard>
          )}
          {showsWithoutCompetition && (
            <SelectableCard
              key="without-competition"
              isSelected={withoutCompetition}
              gridColumn={{ md: "1 / -1" }}
              onClick={() => updateField({ publicContext: { procedureType: ProcedureType.WithoutCompetition } })}
            >
              <Text fontWeight="semibold" fontSize="sm" color="smText">
                {t(ProcedureType.WithoutCompetition)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("Award directly to one supplier when the regulation allows no competition")}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
                {underUtilities ? t("forsyningsforskriften § 9-3") : t("FOA § 13-4")}
              </Text>
            </SelectableCard>
          )}
        </SimpleGrid>
      </FormControl>

      {withoutCompetition && (
        <Box mb="8">
          <NoCompetitionJustificationField
            eventId={eventId}
            justification={publicContext?.noCompetitionJustification ?? null}
            regulation={regulation}
            contestBelowEeaTier={followsDesignContestBelowEeaTier(publicContext)}
            isEditable={state.status === "DRAFT"}
          />
        </Box>
      )}

      {designContest && publicContext?.procedureType && (
        <Box mb="8">
          <DesignContestField
            eventId={eventId}
            contest={publicContext.designContest ?? null}
            procedureType={publicContext.procedureType}
            regulation={regulation}
            foaPart={publicContext.foaPart ?? null}
            homeDepartmentId={state.homeDepartment?.id ?? null}
            isEditable={state.status === "DRAFT"}
          />
        </Box>
      )}

      {withoutNotice && (
        <Box mb="8">
          <NegotiationWithoutNoticeField
            eventId={eventId}
            negotiation={publicContext?.negotiationWithoutNotice ?? null}
            regulation={regulation}
            contestBelowEeaTier={followsDesignContestBelowEeaTier(publicContext)}
            isEditable={state.status === "DRAFT"}
          />
        </Box>
      )}

      <FormControl
        mb="8"
        isInvalid={!!validityError}
        display={
          isTwoStageProcedure(publicContext?.procedureType) || withoutCompetition || designContest ? "none" : undefined
        }
      >
        <FormLabelWithInfo
          tooltip={t("Specifies how long suppliers are bound by their bid. Common practice is 3 or 6 months.")}
          suffix={isSaving("publicContext.eFormsNoticeData.tenderValidityMonths") && <Spinner size="xs" ml="1" />}
        >
          {t("Tender validity period")}
        </FormLabelWithInfo>
        <InputGroup maxW="250px">
          <NumberInput
            value={validityStr}
            clampValueOnBlur={false}
            keepWithinRange={false}
            w="full"
            onChange={(valueAsString, valueAsNumber) => {
              setValidityStr(valueAsString);
              if (!isNaN(valueAsNumber)) {
                updateField(
                  { publicContext: { eFormsNoticeData: { tenderValidityMonths: valueAsNumber } } },
                  { debounceMs: 300 },
                );
              }
            }}
          >
            <NumberInputField borderRightRadius="0" />
          </NumberInput>
          {/* t('month_one') t('month_other') */}
          <InputRightAddon color="smMuted" fontSize="sm">
            {t("month", { count: Number(validityStr) || 1 })}
          </InputRightAddon>
        </InputGroup>
        {validityError && (
          <FormErrorMessage>{formatFieldError(validityError)}</FormErrorMessage>
        )}
      </FormControl>

      {isDpsScheme(publicContext?.procedureType) && (
        <DpsSchemeFields
          eventId={eventId}
          usage={publicContext?.dynamicPurchasingSystem ?? null}
          scheme={publicContext?.dpsScheme ?? null}
          isEditable={state.status === "DRAFT"}
        />
      )}

      {underUtilities &&
        (publicContext?.procedureType === ProcedureType.Restricted ||
          publicContext?.procedureType === ProcedureType.NegotiatedWithPriorCall) && (
        <PriorInformationCallField
          eventId={eventId}
          call={publicContext?.priorInformationCall ?? null}
          isEditable={state.status === "DRAFT"}
        />
      )}

      {isQualificationScheme(publicContext?.procedureType) && (
        <QualificationSchemeFields
          eventId={eventId}
          scheme={publicContext?.qualificationScheme ?? null}
          isEditable={state.status === "DRAFT"}
        />
      )}

      {/* The stage-1 deadline (frist for forespørsel om å delta) is set and confirmed directly
          on its node in the milestone plan, like the bid deadline for single-stage. */}
      {!withoutCompetition && <MilestonePlanCard event={state} />}

      {/* Nedvalg follows the plan: first when the request phase ends, then how many proceed.
          § 26-4 forbids capping a DPS — every qualified supplier is admitted. */}
      {isTwoStageProcedure(publicContext?.procedureType) &&
        !isDpsScheme(publicContext?.procedureType) &&
        !isQualificationScheme(publicContext?.procedureType) && (
        <Box mb="8">
          <CandidateLimitsField
            eventId={eventId}
            current={publicContext?.candidateLimits ?? null}
            statutoryMin={minCandidatesWhenCapped(
              publicContext?.regulation,
              publicContext?.foaPart,
              publicContext?.procedureType,
            )}
            underFoaPartIi={isFoaNationalTier(publicContext?.regulation, publicContext?.foaPart)}
          />
        </Box>
      )}

      {allowsNegotiation(publicContext?.procedureType) && !withoutNotice &&
        (requiresProcedureJustification(regulation) || offersAwardOnInitialTenders(publicContext?.procedureType)) && (
        <Box mb="8">
          <NegotiationTermsField
            eventId={eventId}
            justification={publicContext?.procedureJustification ?? null}
            noNegotiationNecessary={publicContext?.noNegotiationNecessary ?? null}
            requiresJustification={requiresProcedureJustification(regulation)}
            offersAwardOnInitialTenders={offersAwardOnInitialTenders(publicContext?.procedureType)}
          />
        </Box>
      )}

      {isFoaPartII(publicContext?.regulation, publicContext?.foaPart) && (
        <Box mb="8">
          <DialoguePlanField
            eventId={eventId}
            dialoguePlanned={publicContext?.dialoguePlanned ?? null}
            dialogueDescription={publicContext?.dialogueDescription ?? null}
          />
        </Box>
      )}

      {!withoutCompetition && (
        <Box mb="8">
          <SubmissionLanguagesField eventId={eventId} />
        </Box>
      )}

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      <ComingSoonModal featureName={comingSoon} onClose={() => setComingSoon(null)} />
    </Box>
  );
};
